import React from 'react'
import { Link } from 'react-router-dom'
import { C, fOutfit, fBody, fMono, PublicNav, PublicFooter } from '../../Shared/Brand'

const MARKETS = [
  { name: 'Forex Pairs', desc: 'Major, minor, and exotic currency pairs with tight spreads.', to: '/trading/forex-pairs' },
  { name: 'Precious Metals', desc: 'Gold and silver with flexible leverage.', to: '/trading/precious-metals' },
  { name: 'Energies', desc: 'Brent and WTI crude oil CFDs.', to: '/trading/energies' },
  { name: 'Indices', desc: 'Major global indices in a single position.', to: '/trading/indices' },
  { name: 'Cryptocurrencies', desc: 'Direct crypto CFDs, or automated arbitrage via the Arbitrade Bot.', to: '/trading/cryptocurrencies' },
]

const ACCOUNT_TYPES = [
  { name: 'STD', desc: 'Spread-only pricing, no commission.', to: '/trading/std-account' },
  { name: 'ECN', desc: 'Raw spreads plus transparent commission.', to: '/trading/ecn-account' },
  { name: 'PRO', desc: 'Our tightest spreads for high volume.', to: '/trading/pro-account' },
]

const TIERS = [
  { name: 'Starter', range: '$100 – $999', cap: 'Up to 1.5% daily', features: ['Spot arbitrage access', 'Standard bot priority', 'Email support'] },
  { name: 'Growth', range: '$1,000 – $9,999', cap: 'Up to 2.0% daily', features: ['Spot + futures arbitrage', 'Priority bot execution', 'Live chat support'], highlight: true },
  { name: 'Pro', range: '$10,000+', cap: 'Up to 2.5% daily', features: ['Full market access', 'Dedicated execution lane', 'Priority support & account manager'] },
]

const CONDITIONS = [
  { label: 'Execution Fee', value: 'Included in bot spread — no separate commission' },
  { label: 'Withdrawal Processing', value: 'Standard network fee only, processed within 24h' },
  { label: 'Minimum Deposit', value: '$100 USDT equivalent' },
  { label: 'Payout Frequency', value: 'Daily, credited automatically to your dashboard balance' },
]

function SectionTitle({ eyebrow, title, desc }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
      <div style={{ fontSize: '11px', fontFamily: fMono, letterSpacing: '0.14em', color: C.primary, textTransform: 'uppercase', marginBottom: '10px' }}>{eyebrow}</div>
      <h2 style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: 'clamp(28px,4vw,38px)', lineHeight: 1.1, color: C.tx, margin: '0 0 12px' }}>{title}</h2>
      {desc && <p style={{ fontSize: '16px', fontWeight: 500, color: C.tx2, lineHeight: 1.3, maxWidth: '600px', margin: '0 auto' }}>{desc}</p>}
    </div>
  )
}

export default function TradingPage() {
  return (
    <div style={{ background: C.white, color: C.tx, fontFamily: fBody, minHeight: '100vh' }}>
      <style>{`
        .xt-markets-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
        .xt-tiers-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 20px; }
        .xt-cond-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px; }
        @media (max-width: 640px) { .xt-hero-title { font-size: 30px !important; } }
      `}</style>
      <PublicNav active="Trading" />

      {/* HERO — dark */}
      <section style={{ padding: '140px 24px 80px', textAlign: 'center', background: C.dark, color: C.ltx, borderBottom: `1px solid ${C.dbdr}` }}>
        <div style={{ fontSize: '11px', fontFamily: fMono, letterSpacing: '0.14em', color: C.primary, textTransform: 'uppercase', marginBottom: '14px' }}>Trading</div>
        <h1 className="xt-hero-title" style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: 'clamp(34px,5vw,52px)', lineHeight: 1.1, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
          A Dynamic Trading Ecosystem
        </h1>
        <p style={{ fontSize: '16px', fontWeight: 500, color: C.ltx2, maxWidth: '640px', margin: '0 auto 28px', lineHeight: 1.4 }}>
          Trade forex, precious metals, energies, indices, and cryptocurrencies from one account — or let the Xperia Arbitrade Bot scan 50+ exchanges around the clock and execute crypto arbitrage automatically.
        </p>
        <Link to="/signup" style={{ display: 'inline-block', background: C.primary, color: C.dark, fontWeight: 700, fontSize: '14px', padding: '13px 28px', borderRadius: '999px', textDecoration: 'none' }}>Start Trading</Link>
      </section>

      {/* MARKET INSTRUMENTS — light */}
      <section style={{ padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' }}>
        <SectionTitle eyebrow="Market Instruments" title="Markets You Can Trade" desc="Five asset classes, one account — explore each market's conditions in detail." />
        <div className="xt-markets-grid">
          {MARKETS.map(m => (
            <Link key={m.name} to={m.to} style={{ display: 'block', textDecoration: 'none', background: C.light, border: `1px solid ${C.bdr}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: '14px', color: C.tx, marginBottom: '8px' }}>{m.name}</div>
              <p style={{ fontSize: '13px', color: C.tx2, lineHeight: 1.6, margin: 0 }}>{m.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* ACCOUNT TYPES — light */}
      <section style={{ padding: '40px 24px 0', maxWidth: '1200px', margin: '0 auto' }}>
        <SectionTitle eyebrow="Account Types" title="Choose Your Account" desc="Compare STD, ECN, and PRO accounts side by side, or explore each in detail." />
        <div className="xt-markets-grid" style={{ marginBottom: '20px' }}>
          {ACCOUNT_TYPES.map(a => (
            <Link key={a.name} to={a.to} style={{ display: 'block', textDecoration: 'none', background: C.light, border: `1px solid ${C.bdr}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: '14px', color: C.tx, marginBottom: '8px' }}>{a.name} Account</div>
              <p style={{ fontSize: '13px', color: C.tx2, lineHeight: 1.6, margin: 0 }}>{a.desc}</p>
            </Link>
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <Link to="/trading/account-comparison" style={{ fontSize: '13px', color: C.primaryDark, textDecoration: 'underline' }}>Compare all account types →</Link>
        </div>
      </section>

      {/* SUBSCRIPTION TIERS — light, tinted */}
      <section style={{ padding: '80px 24px', borderTop: `1px solid ${C.bdr}`, background: C.light, marginTop: '60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <SectionTitle eyebrow="Arbitrade Bot" title="Subscription Tiers" desc="Choose a tier based on deposit size — higher tiers unlock priority execution and higher daily caps." />
          <div className="xt-tiers-grid">
            {TIERS.map(t => (
              <div key={t.name} style={{
                background: t.highlight ? 'rgba(247,147,26,0.08)' : C.white,
                border: `1px solid ${t.highlight ? C.primary : C.bdr}`, borderRadius: '16px', padding: '28px',
              }}>
                <div style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: '18px', color: t.highlight ? C.primaryDark : C.tx, marginBottom: '4px' }}>{t.name}</div>
                <div style={{ fontSize: '13px', color: C.tx3, marginBottom: '16px' }}>{t.range}</div>
                <div style={{ fontSize: '22px', fontWeight: 700, fontFamily: fOutfit, color: C.tx, marginBottom: '18px' }}>{t.cap}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                  {t.features.map(f => (
                    <div key={f} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', color: C.tx2 }}>
                      <span style={{ color: C.up }}>✓</span>{f}
                    </div>
                  ))}
                </div>
                <Link to="/signup" style={{
                  display: 'block', textAlign: 'center', fontSize: '13px', fontWeight: 600, textDecoration: 'none',
                  padding: '11px 0', borderRadius: '999px',
                  background: t.highlight ? C.primary : 'transparent', color: t.highlight ? C.dark : C.tx2,
                  border: t.highlight ? 'none' : `1px solid ${C.bdr}`,
                }}>Get Started</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRADING CONDITIONS — light */}
      <section style={{ padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' }}>
        <SectionTitle eyebrow="Trading Conditions" title="Fees & Payouts" desc="Transparent, predictable costs — no hidden commissions." />
        <div className="xt-cond-grid">
          {CONDITIONS.map(c => (
            <div key={c.label} style={{ background: C.light, border: `1px solid ${C.bdr}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ fontSize: '12px', color: C.tx3, marginBottom: '8px' }}>{c.label}</div>
              <div style={{ fontSize: '14px', color: C.tx, fontWeight: 600 }}>{c.value}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: '12px', color: C.tx3, marginTop: '24px', textAlign: 'center' }}>
          Daily percentage caps are targets based on prevailing market conditions and are not guaranteed. Trading involves risk — see our <a href="#" style={{ color: C.tx2, textDecoration: 'underline' }}>Risk Disclosure</a>.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
          <Link to="/trading/leverage" style={{ fontSize: '13px', fontWeight: 700, border: `1px solid ${C.bdr}`, color: C.tx2, padding: '10px 22px', borderRadius: '999px', textDecoration: 'none' }}>View Leverage</Link>
          <Link to="/trading/pricing" style={{ fontSize: '13px', fontWeight: 700, border: `1px solid ${C.bdr}`, color: C.tx2, padding: '10px 22px', borderRadius: '999px', textDecoration: 'none' }}>View Full Pricing</Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}
